import {
  Component,
  OnInit,
  Input,
  forwardRef,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { dateAsString } from '../../date.helper';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-datepicker-input',
  templateUrl: './datepicker-input.component.html',
  styleUrls: ['./datepicker-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerInputComponent),
      multi: true,
    },
  ],
})
export class DatepickerInputComponent implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {
  @Input() validationClass: string;
  @Input() dateConfig: any;
  @Input() locale: any;
  @Input() inputLabel: string;
  @Input() singleDatePicker: boolean = true;
  @Input() alwaysShowCalendars: boolean = true;
  @Input() showDropdowns: boolean = true;
  @Input() autoApply: boolean = true;
  @Input() formControl: FormControl;
  @Input() data: any;
  @Input() ngModelType: any;
  @Input() validationStyle: boolean = true;
  @Input() yearsValidations: boolean = false;

  @Output() valueChange = new EventEmitter<any>();

  //for inner calendar date selector
  calendarFormControl: UntypedFormGroup;

  private valueChangesSubscription: Subscription;
  private statusChangesSubscription: Subscription;
  futureTwoYears = new Date();
  pastTwoYears = new Date();
  value: any;
  disabled: boolean = false;
  validDatePattern = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(19|20)\d{2}$/;
  open = false;
  constructor(
    private cdr: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
  ) {
    //calendarFormControl is the formControl for the datepicker
    this.calendarFormControl = this.formBuilder.group({
      date: null,
    });
  }

  ngOnInit(): void {
    this.initializeDates();

    if (!this.locale) {
      this.locale = {
        parentEl: 'body',
        format: 'MM-DD-YYYY',
        displayFormat: 'MM-DD-YYYY',
      };
    }

    if (this.calendarFormControl) {
      this.valueChangesSubscription = this.calendarFormControl.valueChanges.subscribe(value => {
        this.handleValueChange(value.date.startDate ? new Date(value.date.startDate) : null);
      });
    }
    if (this.formControl) {
      this.valueChangesSubscription = this.formControl.valueChanges.subscribe(value => {
        if (value) {
          this.calendarFormControl.get('date').setValue(
            {
              startDate: dateAsString(value),
              endDate: dateAsString(value),
            },
            { emitEvent: false },
          );
        }
      });
      this.setDisabledState(this.formControl.disabled);
      this.statusChangesSubscription = this.formControl.statusChanges.subscribe(() => {
        this.setDisabledState(this.formControl.disabled);
      });
    }
  }

  // we can use this to set the value from FormControl and also from NgModel
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.updateFormControlValue(this.data);
    }
    if (changes.formControl) {
      if (this.formControl && this.formControl.value) {
        this.calendarFormControl.get('date').setValue(
          {
            startDate: dateAsString(this.formControl.value),
            endDate: dateAsString(this.formControl.value),
          },
          { emitEvent: false },
        );
      }
      if (this.formControl) {
        this.setDisabledState(this.formControl.disabled);
      }
    }
  }

  //set the value for formControl
  handleValueChange(value: any) {
    this.formControl.setValue(value, { emitEvent: false });
  }

  onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    // Validate the date pattern
    const isValid = this.validDatePattern.test(value);
    this.formControl.setErrors({ invalidDate: !isValid });
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    // Update the calendar form control based on the input value
    if (!value) {
      this.calendarFormControl.get('date').setValue(null);
    } else if (isValid) {
      this.calendarFormControl.get('date').setValue({ startDate: value, endDate: value });
    }
    this.cdr.detectChanges();
  }

  isInvalidDate(date) {
    return false;
  }

  setForm(data) {
    this.valueChange.emit(data.startDate);
  }

  updateFormControlValue(data: any) {
    //set the value for NgModel Implementation
    if (this.ngModelType) {
      if (data && !this.formControl) {
        this.data = {
          startDate: data.startDate ? data.startDate : data,
          endDate: data.startDate ? data.startDate : data,
        };
      }
    } else {
      //set the value for FormControl Implementation
      this.calendarFormControl.setValue(
        {
          startDate: this.formControl.value,
          endDate: this.formControl.value,
        },
        { emitEvent: false },
      );
    }
    this.cdr.detectChanges();
  }

  initializeDates() {
    this.pastTwoYears.setFullYear(this.pastTwoYears.getFullYear() - 2);
    this.pastTwoYears.setHours(0, 0, 0, 0);
    this.futureTwoYears.setFullYear(this.futureTwoYears.getFullYear() + 2);
    this.futureTwoYears.setHours(23, 59, 59, 999);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${day}-${year}`;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    const dateControl = this.calendarFormControl?.get('date');
    if (dateControl) {
      if (isDisabled) {
        dateControl.disable({ emitEvent: false });
      } else {
        dateControl.enable({ emitEvent: false });
      }
    }
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  getValidationClass(control: FormControl): any {
    if (this.validationStyle) {
      return {
        'is-valid': control && control.valid && (control.dirty || control.touched),
        'is-invalid': control && control.invalid && (control.dirty || control.touched),
      };
    }
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
    if (this.statusChangesSubscription) {
      this.statusChangesSubscription.unsubscribe();
    }
  }
}
